import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, Box, Text } from '@bonusly/design-system';

import { AdminLink } from './AdminLink';
import { NavSection } from './NavSection';
import { NavConfig } from './types/nav';

interface NavProps {
	nav: NavConfig;
}

export const Nav: FC<NavProps> = ({ nav }) => {
	const { t } = useTranslation('vertical_navbar');
	const [openSections, setOpenSections] = useState<number[]>([]);
	const internalNavigationRef = useRef(false);

	// Update open sections when nav.activeSectionIndex changes (URL changes)
	useEffect(() => {
		if (nav.activeSectionIndex !== undefined) {
			setOpenSections((prev) => {
				// If this was triggered by internal navigation (clicking nav links),
				// just ensure the active section is open without closing others
				if (internalNavigationRef.current) {
					internalNavigationRef.current = false;

					if (prev.includes(nav.activeSectionIndex as number)) {
						return prev;
					}

					return [...prev, nav.activeSectionIndex as number];
				}

				// External navigation: close sections that don't contain active links
				const activeSectionIndex = nav.activeSectionIndex as number;
				const shouldCloseSection = (sectionIndex: number) => {
					if (sectionIndex === activeSectionIndex) {
						return false;
					}

					// Check if any link in this section matches the current URL
					const section = nav.sections[sectionIndex];

					if (!section) {
						return true;
					}

					const currentPath = window.location.pathname;
					return !section.links?.some((link) => {
						// Extract path from URL if needed
						let linkPath = link.url;
						if (link.url.includes('http')) {
							try {
								const url = new URL(link.url);
								linkPath = url.pathname;
							} catch (e) {
								linkPath = link.url;
							}
						}

						// Check for matches
						if (linkPath === currentPath) return true;
						if (link.matchActive && link.matchActive.test(currentPath)) return true;
						return false;
					});
				};

				// Filter out sections that should be closed and ensure active section is open
				const newOpenSections = prev.filter((index) => !shouldCloseSection(index));
				if (!newOpenSections.includes(activeSectionIndex)) {
					newOpenSections.push(activeSectionIndex);
				}
				return newOpenSections;
			});
		}
	}, [nav.activeSectionIndex, nav.sections]);

	// Handle accordion changes from user interaction
	const handleAccordionChange = (expandedIndex: number | number[] | undefined) => {
		if (Array.isArray(expandedIndex)) {
			setOpenSections(expandedIndex);
		} else if (typeof expandedIndex === 'number') {
			setOpenSections([expandedIndex]);
		} else {
			setOpenSections([]);
		}
	};

	// Mark navigation as internal when nav links are clicked
	const handleNavLinkClick = () => {
		internalNavigationRef.current = true;
	};

	if (!nav.sections.length) {
		return null;
	}

	return (
		<Box
			data-testid={nav.id}
			borderBottomColor="border.default"
			borderBottomWidth={1}
			pt={3}
			pb={1}
			_last={{
				borderBottomColor: 'transparent',
			}}
		>
			<Text color="text.softest" fontWeight="medium" fontSize={1} pl={3} pb={2}>
				{t(nav.title as any)}
			</Text>

			{nav.links?.map((link) => {
				return (
					<AdminLink key={link.url} icon={link.icon ? <link.icon /> : <></>} to={link.url} mb={2} onClick={handleNavLinkClick}>
						{t(link.label as any)}
					</AdminLink>
				);
			})}

			<Accordion allowMultiple as="ul" aria-label={t(nav.label as any)} role="menu" borderRadius="sm" index={openSections} onChange={handleAccordionChange}>
				{nav.sections.map((section, index) => {
					return <NavSection key={index} section={section} onNavLinkClick={handleNavLinkClick} />;
				})}
			</Accordion>
		</Box>
	);
};
